// The Storyteller's running order for the night: who to visit, in what order, and which player
// holds each character — plus whatever they have submitted, live. Storytellers only, which is
// enforced by the projection rather than by this component: a player's view has no
// `view.storyteller` at all, so there is nothing here to render for them.

import { Check, SkipForward, Undo2 } from "lucide-react";
import type { GameView } from "@cc/shared";
import { useGame } from "../../state/game.js";
import { characterName, cn } from "../../lib/ui.js";
import { Button } from "../ui/primitives.js";
import { Panel } from "./Panel.js";

/** The character's token, with an empty same-sized box when we have no art so rows stay aligned. */
function CharacterIcon({ view, characterId }: { view: GameView; characterId: string }) {
  const icon = view.script.characters.find((c) => c.id === characterId)?.icon ?? null;
  return icon ? (
    <img src={icon} alt="" className="size-10 shrink-0 object-contain" />
  ) : (
    <span className="size-10 shrink-0" />
  );
}

type ReferenceEntry = { characterId: string; characterName: string; seatName: string; order: number };

/**
 * The in-play characters that wake next night, in order: first-night order before night 1, other-
 * nights order afterwards. Built from the Storyteller's own view.
 */
function buildReference(view: GameView): ReferenceEntry[] {
  const firstNight = view.phase.kind === "setup";
  const entries: ReferenceEntry[] = [];
  for (const seat of view.seats) {
    const character = view.script.characters.find((c) => c.id === seat.characterId);
    if (!character) continue;
    const order = firstNight
      ? (character.nightAction?.firstNight ?? character.firstNightWake)
      : (character.nightAction?.otherNight ?? character.otherNightWake);
    if (order === null || order === undefined) continue;
    entries.push({
      characterId: character.id,
      characterName: character.name,
      seatName: seat.displayName,
      order,
    });
  }
  return entries.sort((a, b) => a.order - b.order);
}

export function NightOrder({
  view,
  open,
  onOpenChange,
}: {
  view: GameView;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const send = useGame((s) => s.send);
  const night = view.storyteller?.night;
  if (!night) return null;

  const isNight = view.phase.kind === "night";
  const remaining = night.entries.filter((e) => e.status === "pending" && !e.dormant).length;

  // Outside the night this is a reference sheet, not a working list: live Confirm/Skip buttons on
  // last night's queue would invite meaningless clicks.
  const reference = isNight ? [] : buildReference(view);

  function describePicks(picks: string[]): string {
    return picks
      .map((value) => {
        const seat = view.seats.find((s) => s.id === value);
        if (seat) return seat.displayName;
        return characterName(view, value) ?? value;
      })
      .join(", ");
  }

  return (
    <Panel
      title="Night Order"
      badge={
        <span className="text-sm text-parchment-dim">
          {isNight
            ? `Night ${view.phase.number}${remaining > 0 ? ` · ${remaining} left` : ""}`
            : view.phase.kind === "setup"
              ? "first night"
              : "next night"}
        </span>
      }
      open={open}
      onOpenChange={onOpenChange}
    >
      {!isNight ? (
        reference.length === 0 ? (
          <p className="py-2 text-sm text-parchment-dim">
            Nobody in play wakes {view.phase.kind === "setup" ? "on the first night" : "at night"}.
          </p>
        ) : (
          <ol className="space-y-1">
            {reference.map((entry) => (
              <li key={`${entry.seatName}:${entry.characterId}`} className="flex items-center gap-2 px-2 py-1">
                <CharacterIcon view={view} characterId={entry.characterId} />
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium text-parchment">{entry.characterName}</div>
                  <div className="truncate text-xs text-parchment-dim">{entry.seatName}</div>
                </div>
              </li>
            ))}
          </ol>
        )
      ) : night.entries.length === 0 ? (
        <p className="py-2 text-sm text-parchment-dim">Nobody in play wakes tonight.</p>
      ) : (
        <ol className="space-y-1">
          {night.entries.map((entry, index) => (
            <li
              key={entry.id}
              className={cn(
                "rounded border px-2 py-1.5",
                index === night.cursor ? "border-brass/70 bg-brass/10" : "border-transparent",
                (entry.status !== "pending" || entry.dormant) && "opacity-50",
                entry.dormant && "grayscale",
              )}
            >
              <div className="flex items-center justify-between gap-2">
                <button
                  type="button"
                  onClick={() => void send("st:nightCursor", { cursor: index })}
                  className="flex min-w-0 items-center gap-2 text-left"
                >
                  <CharacterIcon view={view} characterId={entry.characterId} />
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium text-parchment">
                      {entry.characterName}
                      {entry.status === "skipped" && <span className="ml-1 text-parchment-dim">(skipped)</span>}
                    </span>
                    <span className="block truncate text-xs text-parchment-dim">
                      {entry.seatName ?? "unseated"}
                    </span>
                    {entry.dormant && (
                      <span className="block truncate text-xs italic text-parchment-dim">Dead — no action</span>
                    )}
                    {entry.feedback && (
                      <span className="block text-xs italic text-evil">{entry.feedback}</span>
                    )}
                    {!entry.dormant && entry.picks.length > 0 && (
                      <span className="block truncate text-xs text-brass">chose {describePicks(entry.picks)}</span>
                    )}
                    {!entry.dormant && entry.picks.length === 0 && entry.prompt && (
                      <span className="block truncate text-xs italic text-parchment-dim/70">waiting on a pick</span>
                    )}
                  </span>
                </button>
                <div className="flex shrink-0 gap-1">
                  {entry.dormant ? null : entry.status === "pending" ? (
                    <>
                      <Button
                        size="sm"
                        variant="ghost"
                        title="Confirm"
                        onClick={() => void send("st:nightEntry", { entryId: entry.id, action: "confirm" })}
                      >
                        <Check className="size-3.5" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        title="Skip"
                        onClick={() => void send("st:nightEntry", { entryId: entry.id, action: "skip" })}
                      >
                        <SkipForward className="size-3.5" />
                      </Button>
                    </>
                  ) : (
                    <Button
                      size="sm"
                      variant="ghost"
                      title="Put back on the list"
                      onClick={() => void send("st:nightEntry", { entryId: entry.id, action: "reset" })}
                    >
                      <Undo2 className="size-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ol>
      )}
    </Panel>
  );
}
